/**
 * WastewaterAnalyticsSection
 *
 * Complete wastewater sector page — the reference implementation for all future
 * sector pages in ClimatePlans.  Drop this component inside Other.tsx in place of
 * the previous static ChartLayout block.
 *
 * Architecture:
 *   SectionWrapper (parent, Other.tsx)
 *     └─ WastewaterAnalyticsSection
 *          ├─ ChartLayout (chartSelector, keyMetrics, chartContent, 4 narrative tabs)
 *          └─ Internal data fetching (cached; no duplicate Supabase calls)
 *
 * Data sources:
 *   • v_wastewater_inventory_summary  → wastewaterDataService (summary + GHG split)
 *   • population table                → time series scaling
 *   • ghg_inventory_precalculated     → community share chart + percentile + peers
 *   • wastewater_activity             → treatment breakdown
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChartLayout, EquityContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import WastewaterTimeSeriesChart from '../charts/WastewaterTimeSeriesChart';
import WastewaterGHGBreakdownChart from '../charts/WastewaterGHGBreakdownChart';
import WastewaterCommunityShareChart, { type SectorEmissions } from '../charts/WastewaterCommunityShareChart';
import WastewaterPeerComparisonChart from '../charts/WastewaterPeerComparisonChart';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import {
  fetchWastewaterSummary,
  formatWastewaterTierLabel,
  getWastewaterDisplayNote,
  // shouldShowWastewaterLowConfidence — removed (tier now in Methods tab)
  type WastewaterSummary,
  type WastewaterSummaryRow,
} from '../../services/wastewaterDataService';
import {
  fetchWastewaterTimeSeries,
  fetchWastewaterStatewidePercentile,
  fetchWastewaterPeerCities,
  // fetchWastewaterTreatmentBreakdown — removed, wastewater_activity not yet public
  type WastewaterTimeSeries,
  type WastewaterPercentileResult,
  type WastewaterPeerCity,
  // WastewaterTreatmentPoint — removed with treatment tab
} from '../../services/wastewaterAnalyticsService';
import {
  generateWastewaterOverviewNarrative,
} from '../../services/wastewaterNarrativeService';
import { WASTEWATER_NOT_YET_CALCULATED } from '../../constants/methodologyNotes';
import { WASTEWATER_COLOR_HEX } from '../../theme/navChartPalette';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CommunityInventory {
  /** total_emissions_with_wastewater (or total_emissions if wastewater column absent) */
  total: number | null;
  year: number;
}

// ─── Chart view options ──────────────────────────────────────────────────────

const CHART_VIEWS = [
  { value: 'timeline',    label: 'Over Time' },
  { value: 'ghg_species', label: 'CH₄ / N₂O' },
  { value: 'community',   label: 'Community Share' },
  { value: 'peers',       label: 'Peer Cities' },
] as const;

type ChartView = (typeof CHART_VIEWS)[number]['value'];

// ─── (Treatment table removed — wastewater_activity not yet public) ──────────

// ─── Narrative paragraph renderer ────────────────────────────────────────────

const NarrativeParagraphs: React.FC<{ paragraphs: string[] }> = ({ paragraphs }) => (
  <>
    {paragraphs.map((p, i) =>
      p.includes('\n') ? (
        <div key={i} style={{ marginBottom: 12 }}>
          {p.split('\n').map((line, j) => (
            <p key={j} style={{ margin: '2px 0', color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
              {line}
            </p>
          ))}
        </div>
      ) : (
        <p key={i} style={{ marginBottom: 12, color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
          {p}
        </p>
      )
    )}
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface WastewaterAnalyticsSectionProps {
  location: string;
}

const WastewaterAnalyticsSection: React.FC<WastewaterAnalyticsSectionProps> = ({ location }) => {
  const { locationSlug } = useParams<{ locationSlug: string }>();

  // ── State ──────────────────────────────────────────────────────────────────

  const [activeChartView, setActiveChartView] = useState<ChartView>('timeline');
  const [activeNarrativeView, setActiveNarrativeView] = useState<string>('overview');
  const [showPerCapitaTimeline, setShowPerCapitaTimeline] = useState(false);

  // Wastewater summary (primary data)
  const [summary, setSummary] = useState<WastewaterSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // Extended analytics
  const [timeSeries, setTimeSeries] = useState<WastewaterTimeSeries>({
    points: [],
    baselineYear: null,
    baselineEmissions: null,
    pctChange: null,
    allEstimated: true,
  });
  const [percentile, setPercentile] = useState<WastewaterPercentileResult>({
    value: null,
    percentile: null,
    stateMedian: null,
    comparisonCount: 0,
  });
  const [peers, setPeers] = useState<WastewaterPeerCity[]>([]);
  // treatment state removed with treatment tab
  const [community, setCommunity] = useState<CommunityInventory | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // ── Resolved geofips ───────────────────────────────────────────────────────

  const [resolvedGeofips, setResolvedGeofips] = useState<string | null>(null);

  const resolveGeofips = useCallback(async (loc: string, slug?: string): Promise<string | null> => {
    await supabaseLocationService.initialize();
    let gf = await supabaseLocationService.getGeofips(loc);
    if (!gf && slug) {
      const slugInfo = supabaseLocationService.peekLocationBySlug(slug);
      if (slugInfo?.name) gf = await supabaseLocationService.getGeofips(slugInfo.name);
    }
    return gf ? String(gf).padStart(7, '0') : null;
  }, []);

  // ── Primary fetch: wastewater summary ─────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const gf = await resolveGeofips(location, locationSlug);
        if (cancelled) return;
        setResolvedGeofips(gf);
        if (!gf) { setSummary(null); return; }
        const s = await fetchWastewaterSummary(gf);
        if (!cancelled) setSummary(s);
      } catch {
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [location, locationSlug, resolveGeofips]);

  // ── Extended analytics (after geofips known) ──────────────────────────────

  useEffect(() => {
    if (!resolvedGeofips || !summary?.hasData) return;
    let cancelled = false;
    const run = async () => {
      setAnalyticsLoading(true);
      try {
        const row = summary.latestRow;
        const measuredEmissions = new Map(
          summary.rows.map((r) => [r.inventory_year, r.wastewater_emissions_mtco2e])
        );
        const measuredPerCapita = new Map(
          summary.rows.map((r) => [r.inventory_year, r.wastewater_emissions_per_capita])
        );

        // Run parallel fetches (treatment removed — wastewater_activity not yet public)
        const [ts, perc, communityRow] = await Promise.allSettled([
          fetchWastewaterTimeSeries(resolvedGeofips, measuredEmissions, measuredPerCapita),
          fetchWastewaterStatewidePercentile(resolvedGeofips),
          fetchCommunityInventory(resolvedGeofips),
        ]);

        if (cancelled) return;

        if (ts.status === 'fulfilled') setTimeSeries(ts.value);
        if (perc.status === 'fulfilled') setPercentile(perc.value);
        if (communityRow.status === 'fulfilled' && communityRow.value) setCommunity(communityRow.value);

        // Peer cities: estimate population from wastewater per-capita (ghg_precalc has no population column)
        const estPop = row?.wastewater_emissions_per_capita && row.wastewater_emissions_per_capita > 0
          ? Math.round(row.wastewater_emissions_mtco2e / row.wastewater_emissions_per_capita)
          : null;
        const pop = estPop;
        if (pop && pop > 0) {
          const peerResult = await fetchWastewaterPeerCities(resolvedGeofips, pop);
          if (!cancelled) setPeers(peerResult);
        }
      } finally {
        if (!cancelled) setAnalyticsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [resolvedGeofips, summary]);

  // ── Community inventory fetch ─────────────────────────────────────────────

  const row = summary?.latestRow ?? null;

  // ── Community share: wastewater vs rest-of-inventory ─────────────────────

  const sectorEmissions: SectorEmissions[] = useMemo(() => {
    if (!community?.total || !row) return [];
    const ww = row.wastewater_emissions_mtco2e;
    const rest = community.total - ww;
    if (ww <= 0 || rest < 0) return [];
    return [
      { label: 'Wastewater', mtco2e: ww, color: WASTEWATER_COLOR_HEX },
      { label: 'All Other Sectors', mtco2e: rest, color: '#cbd5e1' },
    ];
  }, [community, row]);

  // ── Narrative generation ───────────────────────────────────────────────────

  // Overview text switches with active chart view — kept deliberately short
  const activeOverviewParagraphs = useMemo(
    () =>
      generateWastewaterOverviewNarrative({
        location,
        row,
        percentile,
        totalCommunityEmissions: community?.total ?? null,
        timeSeries,
        activeChartView,
      }),
    [location, row, percentile, community, timeSeries, activeChartView]
  );

  // ── Render helpers ────────────────────────────────────────────────────────

  const displayNote = getWastewaterDisplayNote(row);

  // ── KPI cards ─────────────────────────────────────────────────────────────

  const keyMetricsNode = (
    <KeyMetrics>
      <KeyMetric
        value={
          loading ? '…'
          : row ? `${row.wastewater_emissions_mtco2e.toLocaleString(undefined, { maximumFractionDigits: 0 })} MTCO₂e`
          : '—'
        }
        label={row ? `Wastewater Emissions (${row.inventory_year})` : 'Wastewater Emissions'}
        color={WASTEWATER_COLOR_HEX}
      />
      <KeyMetric
        value={
          loading ? '…'
          : row?.wastewater_emissions_per_capita != null
            ? `${row.wastewater_emissions_per_capita.toLocaleString(undefined, { maximumFractionDigits: 4 })} MTCO₂e`
          : '—'
        }
        label="Per Capita (MTCO₂e)"
        color={WASTEWATER_COLOR_HEX}
      />
      <KeyMetric
        value={
          !community || community.total == null || !row ? '—'
          : (() => {
              const pct = (row.wastewater_emissions_mtco2e / community.total) * 100;
              const digits = pct >= 1 ? 1 : pct >= 0.1 ? 2 : pct >= 0.01 ? 3 : 4;
              return `${pct.toFixed(digits)}%`;
            })()
        }
        label="Share of Community Emissions"
        color={WASTEWATER_COLOR_HEX}
      />
    </KeyMetrics>
  );

  // ── Chart content ─────────────────────────────────────────────────────────

  const spinner = (msg = 'Loading…') => (
    <div style={{ padding: 40, color: '#6b7280', textAlign: 'center', fontSize: 14 }}>{msg}</div>
  );

  const chartContentNode = loading ? (
    spinner('Loading wastewater data…')
  ) : !summary?.hasData ? (
    <div style={{ padding: 32, color: '#6b7280' }}>{WASTEWATER_NOT_YET_CALCULATED}</div>
  ) : (
    <>
      {activeChartView === 'timeline' && (
        analyticsLoading && timeSeries.points.length === 0
          ? spinner('Building time series…')
          : <WastewaterTimeSeriesChart
              points={timeSeries.points}
              location={location}
              showPerCapita={showPerCapitaTimeline}
            />
      )}
      {activeChartView === 'ghg_species' && (
        <WastewaterGHGBreakdownChart row={row} />
      )}
      {activeChartView === 'community' && (
        analyticsLoading && sectorEmissions.length === 0
          ? spinner('Loading community inventory…')
          : <WastewaterCommunityShareChart
              sectors={sectorEmissions}
              year={row?.inventory_year ?? 2020}
              location={location}
            />
      )}
      {activeChartView === 'peers' && (
        analyticsLoading && peers.length === 0
          ? spinner('Loading peer city data…')
          : <WastewaterPeerComparisonChart
              peers={peers}
              thisCity={{
                location_name: row?.jurisdiction_name ?? location,
                wastewater_per_capita: row?.wastewater_emissions_per_capita ?? null,
              }}
            />
      )}
      {/* Treatment process tab removed — wastewater_activity not yet public */}
    </>
  );

  // ── Narrative tabs ────────────────────────────────────────────────────────

  const chartViewLabel = CHART_VIEWS.find((v) => v.value === activeChartView)?.label ?? '';

  const overviewContentNode = (
    <OverviewContent
      title={`Wastewater — ${chartViewLabel}`}
      description={`Municipal wastewater treatment GHG emissions for ${location}.`}
    >
      <NarrativeParagraphs paragraphs={activeOverviewParagraphs} />
    </OverviewContent>
  );

  const relevanceContentNode = (
    <RelevanceContent
      title="Relevance to Climate Action"
      description="How wastewater fits into climate action planning and key reduction opportunities."
    >
      <div className="methodology-details-simple">
        <h5>Utility Partnerships</h5>
        <p>Cities can work with wastewater utilities to set GHG reduction targets, fund process upgrades, and integrate climate goals into service agreements.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Process Optimization</h5>
        <p>Optimizing aeration, nitrogen removal, and solids handling can reduce N₂O and CH₄ generation. Biogas capture from anaerobic digesters converts methane into usable energy.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Water Conservation</h5>
        <p>Reducing indoor water use lowers wastewater volumes, which directly reduces treatment energy and process emissions.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Infiltration & Inflow Reduction</h5>
        <p>Sealing aging sewer lines reduces stormwater entering collection systems, cutting unnecessary treatment volumes and associated emissions.</p>
      </div>
    </RelevanceContent>
  );

  const equityContentNode = (
    <EquityContent
      title="Equity Considerations"
      description="Environmental justice dimensions of wastewater siting and service area attribution."
    >
      <div className="methodology-details-simple">
        <h5>Attribution vs. Location</h5>
        <p>Emissions shown here are attributed to the jurisdiction served, not to where the treatment facility sits. A regional plant may serve multiple cities; each city's share reflects attributed treatment volume.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Facility Siting</h5>
        <p>Wastewater treatment facilities are often sited in lower-income or industrial communities. Infrastructure investments should prioritize upgrading plants in environmental justice areas and mitigating local air quality impacts.</p>
      </div>
    </EquityContent>
  );

  const TIER_ROWS = [
    { tier: 1, meaning: 'Direct metered jurisdiction flow',               confidence: 'Very High Confidence', color: '#10b981' },
    { tier: 2, meaning: 'Facility-attributed measured treatment volumes',  confidence: 'High Confidence',      color: '#10b981' },
    { tier: 3, meaning: 'Estimated facility / service-area volumes',       confidence: 'Moderate Confidence',  color: '#0ea5e9' },
    { tier: 4, meaning: 'Supplier matched, facility inferred',             confidence: 'Moderate Confidence',  color: '#f59e0b' },
    { tier: 5, meaning: 'Statewide per-capita default',                    confidence: 'Preliminary Estimate', color: '#f97316' },
  ];

  const methodologyContentNode = (
    <div>
      <h4>Methodology</h4>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
        Wastewater process emissions use ICLEI US Community Protocol (USCP) Appendix F emission factors.
        Electricity consumed in treatment is excluded — it is counted in the Electricity sector.
        Biosolids transport emissions are not yet included.
      </p>

      <div className="methodology-details">
        <h5>Data Sources</h5>
        <ul>
          <li>Urban Water Management Plans (UWMP 2020) — treatment volumes by facility</li>
          <li>ICLEI USCP Appendix F — process emission factors for CH₄ and N₂O</li>
          <li>US Census annual population estimates — per-capita and time-series scaling</li>
        </ul>

        <h5>How Emissions Are Calculated</h5>
        <ul>
          <li>Treatment volume attributed to the city via supplier-to-facility crosswalk</li>
          <li>Where no facility match: Service Population × 100 gpd daily flow default (Tier 5)</li>
          <li>CH₄ and N₂O calculated separately; converted to MTCO₂e using IPCC AR5 GWPs</li>
          <li>Years outside 2020 scaled from measured baseline using annual population ratios</li>
        </ul>

        <h5>Confidence Tiers</h5>
        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 8 }}>
          Each jurisdiction is assigned a data tier that reflects the quality of the underlying activity data.
          {row ? ` ${location} is currently Tier ${row.best_tier}.` : ''}
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}>Tier</th>
              <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}>Activity Data</th>
              <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {TIER_ROWS.map((t) => (
              <tr
                key={t.tier}
                style={{
                  background: row?.best_tier === t.tier ? `${t.color}18` : 'transparent',
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                <td style={{ padding: '6px 10px', fontWeight: row?.best_tier === t.tier ? 700 : 400, color: t.color }}>
                  Tier {t.tier}{row?.best_tier === t.tier ? ' ←' : ''}
                </td>
                <td style={{ padding: '6px 10px', color: '#374151' }}>{t.meaning}</td>
                <td style={{ padding: '6px 10px', color: t.color, fontWeight: 500 }}>{t.confidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Chart selector ────────────────────────────────────────────────────────

  const additionalTimelineToggle =
    activeChartView === 'timeline'
      ? [
          {
            label: 'Unit',
            options: [
              { value: 'total', label: 'Total MTCO₂e' },
              { value: 'per_capita', label: 'Per Capita' },
            ],
            value: showPerCapitaTimeline ? 'per_capita' : 'total',
            onChange: (v: string) => setShowPerCapitaTimeline(v === 'per_capita'),
          },
        ]
      : [];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ChartLayout
      title="Wastewater"
      description={`Municipal wastewater treatment greenhouse gas emissions for ${location}.`}
      chartType="line"
      keyMetrics={keyMetricsNode}
      chartContent={chartContentNode}
      chartSelector={{
        options: CHART_VIEWS.map((v) => ({ value: v.value, label: v.label })),
        value: activeChartView,
        onChange: (v) => setActiveChartView(v as ChartView),
      }}
      additionalButtonGroups={additionalTimelineToggle}
      overviewContent={overviewContentNode}
      relevanceContent={relevanceContentNode}
      equityContent={equityContentNode}
      methodologyContent={methodologyContentNode}
      activeView={activeNarrativeView}
      onViewChange={setActiveNarrativeView}
      showRelevanceEquity={true}
      enableComments={false}
      location={location}
      appendLocationToTitle={true}
    />
  );
};

// ─── Community inventory fetch (module-level cache) ───────────────────────────

const communityCache = new Map<string, Promise<CommunityInventory | null>>();

async function fetchCommunityInventory(paddedGeofips: string): Promise<CommunityInventory | null> {
  if (communityCache.has(paddedGeofips)) return communityCache.get(paddedGeofips)!;
  const promise = _fetchCommunityInventory(paddedGeofips);
  communityCache.set(paddedGeofips, promise);
  return promise;
}

async function _fetchCommunityInventory(paddedGeofips: string): Promise<CommunityInventory | null> {
  // ghg_inventory_precalculated.geofips is an integer; only fetch the columns we actually use
  const numericGeofips = Number(paddedGeofips);

  // Note: ghg_inventory_precalculated has NO 'population' column — do not select it
  const { data, error } = await supabaseClient
    .from('ghg_inventory_precalculated')
    .select('year,total_emissions,total_emissions_with_wastewater')
    .eq('geofips', numericGeofips)
    .eq('year', 2020)
    .limit(1)
    .maybeSingle() as { data: Record<string, unknown> | null; error: unknown };

  if (error) {
    console.error('[WastewaterAnalyticsSection] Community fetch error:', error);
  }
  if (!data) return null;

  const n = (v: unknown) => (v != null ? Number(v) : null);
  const total = n(data['total_emissions_with_wastewater']) ?? n(data['total_emissions']);

  return {
    total,
    year: Number(data['year'] ?? 2020),
  };
}

export default WastewaterAnalyticsSection;
